import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/**
 * Wrapper layout per le pagine pubbliche del CRM (Privacy Policy, Terms of
 * Service) — pattern preso da feelhippoai/PublicPageLayout.tsx.
 *
 * Struttura:
 *   - Header: brand (logo + nome + tagline), riusa lo SCSS .login-brand
 *     esistente per coerenza visiva con la LoginPage.
 *   - Children: il contenuto della pagina (rich text via i18n).
 *   - Footer (opzionale): link Privacy Policy + Terms of Service separati
 *     da pipe. Disabilitabile via prop `showFooterLinks` per evitare link
 *     self-referenziali (es. Privacy Policy non rilinka a sé stessa, ma
 *     rilinka comunque a Terms).
 *
 * LoginPage NON usa questo layout (ha già il proprio brand header inline).
 * Solo Privacy/Terms lo usano.
 */
interface PublicPageLayoutProps {
  children: ReactNode;
  /**
   * Se diverso da `'privacy'`, mostra il link a Privacy Policy nel footer.
   * Se diverso da `'terms'`, mostra il link a Terms of Service.
   * In pratica le 2 pagine si rilinkano a vicenda ma non a sé stesse.
   */
  selfPage?: 'privacy' | 'terms';
}

export function PublicPageLayout({ children, selfPage }: PublicPageLayoutProps) {
  const { t } = useTranslation();

  return (
    <div className="public-page-layout">
      <div className="public-page-layout__brand login-brand">
        <h1>
          D'vel <span className="login-brand__accent">CRM</span>
        </h1>
        <p className="login-brand__tagline">{t('brand.tagline')}</p>
      </div>

      <div className="public-page-layout__content">{children}</div>

      <div className="public-page-layout__footer">
        {/* Home sempre primo elemento: riporta alla login page. */}
        <Link to="/login" className="public-page-layout__footer-link">
          {t('loginFooter.home')}
        </Link>
        {selfPage !== 'privacy' && (
          <>
            <span className="public-page-layout__footer-sep"> | </span>
            <Link to="/privacy-policy" className="public-page-layout__footer-link">
              {t('loginFooter.privacyPolicy')}
            </Link>
          </>
        )}
        {selfPage !== 'terms' && (
          <>
            <span className="public-page-layout__footer-sep"> | </span>
            <Link to="/terms-of-service" className="public-page-layout__footer-link">
              {t('loginFooter.termsOfService')}
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
